import { Fragment, useMemo, useState } from 'react';
import useGetParams from '../hooks/useGetParams';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import {
  useGetScannerTypesQuery,
  useDeleteScannerTypeMutation,
  useGetBusinessUsersQuery,
  useUpdateUserEventMutation,
  useAddUserToEventMutation,
} from '../store';
import AdminSectionTitle from '../components/AdminSectionTitle';
import ScannerTypesTable from '../components/ScannerTypesTable';
import CustomModal from '../components/atoms/CustomModal';
import CreateEditScannerType from '../components/CreateEditScannerType';
import Button from '../components/atoms/Button';
import { ScannerTypeDTO } from '../models/scanner-type';
import { toastSuccess } from '../utils/toasts';
import LoadingLottie from '../components/atoms/LoadingLottie';
import FilterTable, { BaseData } from '../components/Table/Table';
import UserDTO from '../models/user';

interface ScannerUserRow extends BaseData {
  id: string;
  username: string | JSX.Element;
  email: string;
  scanner_type: JSX.Element;
}

const AdminScannerTypesPage = () => {
  const { event, eventLoading } = useGetSelectedEvent();
  const { eventId, businessId } = useGetParams();
  const { data: scannerTypes, isFetching } = useGetScannerTypesQuery({ businessId, eventId });
  const [deleteScannerType, deleteResult] = useDeleteScannerTypeMutation();
  const { data: businessUsers } = useGetBusinessUsersQuery({ businessId });
  const [updateUserEvent] = useUpdateUserEventMutation();
  const [addUserToEvent] = useAddUserToEventMutation();

  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedScannerType, setSelectedScannerType] = useState<ScannerTypeDTO | undefined>();

  const scannerUsers: UserDTO[] = useMemo(() => {
    if (!businessUsers) {
      return [];
    }
    return businessUsers.filter((u) => {
      const ub = u.user_businesses?.find((b) => b.business_id === businessId);
      return ub?.permissions === 'SCANNER';
    });
  }, [businessUsers, businessId]);

  const handleScannerTypeChange = async (user: UserDTO, scannerTypeId: string | null) => {
    const userEvent = user.user_events?.find((ue) => ue.event_id === eventId);

    try {
      if (userEvent) {
        const res = await updateUserEvent({
          businessId,
          eventId,
          data: {
            id: userEvent.id,
            scanner_type_id: scannerTypeId,
          },
        });
        if (!('error' in res)) {
          toastSuccess('Tipo de escáner actualizado');
        }
      } else {
        const res = await addUserToEvent({
          businessId,
          eventId,
          userData: {
            userId: user.id,
            permissions: 'SCANNER',
            comissionRate: 0,
            active: true,
            scanner_type_id: scannerTypeId,
          },
        });
        if (!('error' in res)) {
          toastSuccess('Escáner asignado al evento');
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const scannerTypeOptions = useMemo(() => [
    { label: 'Sin restricción (todos)', value: '' },
    ...(scannerTypes || [])
      .filter((st) => st.is_active)
      .map((st) => ({ label: st.name, value: st.id })),
  ], [scannerTypes]);

  const scannerUserRows: ScannerUserRow[] = useMemo(() => scannerUsers.map((user) => {
    const userEvent = user.user_events?.find((ue) => ue.event_id === eventId);
    const currentScannerTypeId = userEvent?.scanner_type_id || '';

    return {
      id: user.id,
      username: <span className="whitespace-nowrap">{user.username || user.email}</span>,
      email: user.email,
      scanner_type: (
        <select
          className="min-w-[200px] w-full bg-surface-base border border-border-tertiary rounded-xl px-3 py-2 text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
          value={currentScannerTypeId}
          onChange={(e) => {
            const newValue = e.target.value || null;
            handleScannerTypeChange(user, newValue as string | null);
          }}
        >
          {scannerTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ),
    };
  }), [scannerUsers, eventId, scannerTypeOptions]);

  const scannerUserHeadCells = [
    { id: 'username' as const, label: 'Usuario' },
    { id: 'email' as const, label: 'Email' },
    { id: 'scanner_type' as const, label: 'Tipo de escáner' },
  ];

  const handleAdd = () => {
    setSelectedScannerType(undefined);
    setShowCreateEditModal(true);
  };

  const handleEdit = (scannerType: ScannerTypeDTO) => {
    setSelectedScannerType(scannerType);
    setShowCreateEditModal(true);
  };

  const handleDeleteClick = (scannerType: ScannerTypeDTO) => {
    setSelectedScannerType(scannerType);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedScannerType) {
      return;
    }
    try {
      const res = await deleteScannerType({
        businessId,
        eventId,
        scannerTypeId: selectedScannerType.id,
      });
      if (!('error' in res)) {
        toastSuccess('Tipo de escáner eliminado');
        setShowDeleteModal(false);
        setSelectedScannerType(undefined);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSuccess = () => {
    setShowCreateEditModal(false);
    setSelectedScannerType(undefined);
  };

  if (eventLoading) {
    return <LoadingLottie />;
  }

  return (
    <div className="my-6 flex flex-col gap-8">
      <AdminSectionTitle
        title="Eventos"
        subtitle={`${event?.name ? `${event.name} - Tipos de escáner` : 'Tipos de escáner'}`}
      />
      <div className="glass-surface">
        <h1 className="text-2xl font-medium mb-2">Tipos de escáner</h1>
        <p className="text-text-secondary text-sm mb-4">
          Crea tipos de escáner para restringir qué entradas puede escanear cada escáner. Si no se asigna un tipo, el escáner podrá escanear todas las entradas.
        </p>
        <ScannerTypesTable
          scannerTypes={scannerTypes || []}
          isLoading={isFetching}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      </div>

      {scannerUsers.length > 0 && (
        <div className="glass-surface">
          <h1 className="text-2xl font-medium mb-2">Escáneres del evento</h1>
          <p className="text-text-secondary text-sm mb-4">
            Asigná un tipo de escáner a cada usuario escáner. Si no se asigna, podrá escanear todas las entradas.
          </p>
          <FilterTable
            rows={scannerUserRows}
            headCells={scannerUserHeadCells}
            pagination={{ pageSize: 10, enabled: true }}
            searchEnabled={true}
            tableId="scanner-users-table"
          />
        </div>
      )}

      <CustomModal
        onRequestClose={() => setShowCreateEditModal(false)}
        isOpen={showCreateEditModal}
        hideCancelButton
        overflowVisible
      >
        <Fragment>
          <div className="flex flex-col gap-2 mb-2">
            <h1 className="text-xl font-medium">
              {selectedScannerType ? 'Editar tipo de escáner' : 'Crear tipo de escáner'}
            </h1>
            <p className="text-text-tertiary text-sm">
              {selectedScannerType
                ? 'Modificá los datos de este tipo de escáner'
                : 'Creá un nuevo tipo de escáner para este evento'}
            </p>
          </div>
          <CreateEditScannerType
            businessId={businessId}
            eventId={eventId}
            scannerType={selectedScannerType}
            onSuccess={handleSuccess}
            onClose={() => setShowCreateEditModal(false)}
          />
        </Fragment>
      </CustomModal>

      <CustomModal
        onRequestClose={() => setShowDeleteModal(false)}
        isOpen={showDeleteModal}
        hideCancelButton
      >
        <div className="flex flex-col gap-3">
          <h1 className="text-xl font-medium">Eliminar tipo de escáner</h1>
          <p className="text-text-secondary">
            ¿Estás seguro de que querés eliminar el tipo de escáner &quot;{selectedScannerType?.name}&quot;? Esta acción no se puede deshacer.
          </p>
          <div className="flex flex-row gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleDeleteConfirm}
              loading={deleteResult.isLoading}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default AdminScannerTypesPage;
